import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, XCircle, Loader2, FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { onboardingService } from "@/services";

type NdaStatus = "polling" | "signed" | "declined" | "error";

const POLL_INTERVAL_MS = 5000;

export default function NDACompletePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<NdaStatus>("polling");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getEnvelopeId = useCallback((): string | null => {
    try {
      const raw = localStorage.getItem("echopeak-lead-context");
      if (!raw) return null;
      const ctx = JSON.parse(raw);
      return ctx.ndaEnvelopeId ?? null;
    } catch {
      return null;
    }
  }, []);

  const pollNdaStatus = useCallback(async () => {
    const envelopeId = getEnvelopeId();
    if (!envelopeId) {
      setStatus("error");
      return;
    }

    try {
      const response = await onboardingService.getNdaStatus(envelopeId);

      if (response.status === "signed") {
        setStatus("signed");
        try {
          const raw = localStorage.getItem("echopeak-lead-context");
          if (raw) {
            const ctx = JSON.parse(raw);
            ctx.ndaSigned = true;
            localStorage.setItem(
              "echopeak-lead-context",
              JSON.stringify(ctx),
            );
          }
        } catch {
          // noop
        }
      } else if (response.status === "declined" || response.status === "voided") {
        setStatus("declined");
      }
    } catch {
      // keep polling on transient errors
    }
  }, [getEnvelopeId]);

  useEffect(() => {
    const envelopeId = getEnvelopeId();
    if (!envelopeId) {
      navigate("/verify/nda");
      return;
    }

    pollNdaStatus();
    intervalRef.current = setInterval(pollNdaStatus, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [getEnvelopeId, pollNdaStatus, navigate]);

  useEffect(() => {
    if (status !== "polling" && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [status]);

  const handleContinue = () => {
    navigate("/engage");
  };

  const handleRetry = () => {
    navigate("/verify/nda");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full relative z-10"
      >
        <Card className="glass-card border-gradient">
          <CardContent className="pt-8 pb-6 px-6 md:px-8">
            {status === "polling" && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto">
                  <FileSignature className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-2">
                    Waiting for Your Signature
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Please complete the NDA signing in the DocuSign tab. This
                    page will update automatically once the document is signed.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Checking signature status…</span>
                </div>
              </div>
            )}

            {status === "signed" && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                  <ShieldCheck className="h-8 w-8 text-emerald-400" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-2">
                    NDA Signed Successfully
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Thank you for signing the Non-Disclosure Agreement. Your
                    information is now protected and you can proceed to schedule
                    your discovery call.
                  </p>
                </div>
                <Button onClick={handleContinue} className="w-full" size="lg">
                  Continue
                </Button>
              </div>
            )}

            {(status === "declined" || status === "error") && (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
                <div>
                  <h1 className="font-display text-2xl font-bold mb-2">
                    Signing Incomplete
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    The NDA signing was not completed. You can try again whenever
                    you're ready.
                  </p>
                </div>
                <Button onClick={handleRetry} className="w-full" size="lg">
                  Try Again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
